import type { FC } from "react";

type MatchingResultsPaginationProps = {
  page: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
};

/**
 * マッチング結果ページネーション
 * @param {MatchingResultsPaginationProps} props コンポーネントProps
 * @return {JSX.Element} MatchingResultsPagination コンポーネント
 */
export const MatchingResultsPagination: FC<MatchingResultsPaginationProps> = ({
  page,
  total,
  limit,
  onPageChange,
}) => {
  const totalPages = Math.ceil(total / limit);

  if (totalPages <= 1) return null;

  const getPageNumbers = (): (number | "start-ellipsis" | "end-ellipsis")[] => {
    const pages: (number | "start-ellipsis" | "end-ellipsis")[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("start-ellipsis");
      for (
        let i = Math.max(2, page - 1);
        i <= Math.min(totalPages - 1, page + 1);
        i++
      ) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push("end-ellipsis");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-base-content/60">
        全{total}件中 {(page - 1) * limit + 1}〜{Math.min(page * limit, total)}
        件
      </span>
      <div className="join">
        <button
          type="button"
          className="join-item btn btn-sm"
          disabled={page === 1}
          onClick={() => onPageChange(page - 1)}
        >
          前へ
        </button>
        {getPageNumbers().map(p =>
          typeof p === "string" ? (
            <span key={p} className="join-item btn btn-sm btn-disabled">
              ...
            </span>
          ) : (
            <button
              type="button"
              key={p}
              className={`join-item btn btn-sm ${p === page ? "btn-active" : ""}`}
              onClick={() => onPageChange(p)}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          className="join-item btn btn-sm"
          disabled={page === totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          次へ
        </button>
      </div>
    </div>
  );
};
